import * as v from "valibot";
import { jsonrepair } from "jsonrepair";

import { ascending, sleep } from "@hackclub/lapse-shared";
import { retryable } from "@/safety";

/**
 * The metadata about a stored timelapse. This does *not* include the actual video - invoke `deviceStorage.getTimelapseVideoSessions` for this.
 */
export interface StoredTimelapse {
  startedAt: number;
  snapshots: number[];
  sessions: number[];

  /** When this recording was restored (published). Unset while it's still unpublished. */
  restoredAt?: number;

  /** The restore-logic version it was restored under. Lets us resurface it if a newer version supersedes it. */
  restoredVersion?: number;
}

/**
 * A device with an associated passkey.
 */
export interface LocalDevice {
  id: string;
  passkey: string;
  legacyPasskey?: string;
  thisDevice: boolean;
}

/**
 * Collection of locally stored devices.
 */
export type LocalDevices = LocalDevice[];

export type LocalTimelapse = v.InferInput<typeof LocalTimelapseSchema>;
const LocalTimelapseSchema = v.object({
  startedAt: v.number(),
  snapshots: v.array(v.number()),
  sessions: v.array(v.number()),
  restoredAt: v.optional(v.number()),
  restoredVersion: v.optional(v.number())
});

const DeviceSchema = v.object({
  id: v.string(),
  passkey: v.string(),
  legacyPasskey: v.optional(v.string()),
  thisDevice: v.boolean(),
});

type Store = v.InferOutput<typeof StoreSchema>;
const StoreSchema = v.object({
  devices: v.array(DeviceSchema),
  timelapse: v.nullable(LocalTimelapseSchema),
});

class AsyncQueue {
  private currentTask: Promise<unknown> = Promise.resolve();

  enqueue<T>(task: () => Promise<T>): Promise<T> {
    const promise = this.currentTask.then(() => task());
    this.currentTask = promise.then(
      () => { },
      () => { }
    );

    return promise;
  }

  async synchronize(): Promise<void> {
    return this.enqueue(async () => { });
  }
}

async function doesFileExist(directoryHandle: FileSystemDirectoryHandle, fileName: string) {
  for await (const entry of directoryHandle.values()) {
    if (entry.kind === "file" && entry.name === fileName) {
      return true;
    }
  }

  return false;
}

function handleMissingApi(forceNavigation: boolean = false) {
  console.warn("(deviceStorage.ts) missing API detected; assuming outdated browser");

  if (localStorage.getItem("lapse:bypass_browser_check") === "1") {
    console.warn("(deviceStorage.ts) bypass flag set; skipping update-browser redirect");
    return;
  }

  if (forceNavigation || !location.href.includes("update-browser")) {
    location.href = "/update-browser";
  }
}

/**
 * Securely stores data on the client device.
 */
export class DeviceStorage {
  private root: FileSystemDirectoryHandle | null = null;
  private serialQueue = new AsyncQueue();

  /**
   * Whether this browser exposes the OPFS APIs `DeviceStorage` relies on. Side-effect free, so it can be used to
   * merely probe for recoverable data without triggering `ensureInit`'s redirect to `/update-browser`.
   */
  static isSupported(): boolean {
    return typeof navigator !== "undefined"
      && !!navigator.storage?.getDirectory
      && typeof FileSystemFileHandle !== "undefined"
      && "createWritable" in FileSystemFileHandle.prototype;
  }

  private async ensureInit(): Promise<void> {
    if (this.root)
      return;

    if (!DeviceStorage.isSupported()) {
      handleMissingApi();
      await sleep(1000);
      return;
    }

    if (navigator.storage.persist) {
      const persisted = await navigator.storage.persist();
      console.log(`(deviceStorage.ts) persistence ${persisted ? "granted" : "denied"}`);
    }

    this.root = await navigator.storage.getDirectory();

    const lapseRoot = await this.getLapseDir();
    if (!await doesFileExist(lapseRoot, "store.json")) {
      console.log("(deviceStorage.ts) no OPFS store found - creating a new one");
      await this.writeStore({ devices: [], timelapse: null });
    }
    else {
      console.log("(deviceStorage.ts) existing OPFS store found");
    }

    // Legacy migration redirect removed — migrate.tsx has been deprecated.
  }

  private async getLapseDir(): Promise<FileSystemDirectoryHandle> {
    return await this.root!.getDirectoryHandle("lapse", { create: true });
  }

  private async readStore(): Promise<Store> {
    await this.ensureInit();

    const dir = await this.getLapseDir();
    const fileHandle = await dir.getFileHandle("store.json", { create: true });
    const file = await fileHandle.getFile();

    const contents = await file.text();

    if (!contents) {
      console.warn("(deviceStorage.ts) store.json was empty or missing - reinitializing");
      const store: Store = { devices: [], timelapse: null };
      await this.writeStore(store);
      return store;
    }

    try {
      return v.parse(StoreSchema, JSON.parse(contents));
    }
    catch (err) {
      console.error(`(deviceStorage.ts) JSON store corrupted - rebuilding! ${contents}`, err);
      localStorage.setItem("lapse:corruptedStoreBackup", contents); // just in case...
      
      let restored: any = {};
      try {
        restored = JSON.parse(jsonrepair(contents));
      }
      catch (err) {
        console.warn(`(deviceStorage.ts) could not restore JSON - will rebuild completely. risky!`, err);
      }

      function tryGet<T>(keys: string[], defaultValue: T, coalesce: (x: unknown) => T): T {
        let current: any = restored;
        for (const key of keys) {
          if (
            current == null ||
            (typeof current !== "object" && typeof current !== "function") ||
            !(key in current)
          ) {
            return defaultValue;
          }

          current = current[key];
        }

        return coalesce(current);
      }

      const devices = tryGet(["devices"], [], (x) => {
        if (!Array.isArray(x))
          return [];

        return x.filter(x => "thisDevice" in x && x["thisDevice"]);
      });

      const snapshots = tryGet(["timelapse", "snapshots"], [], (x) => {
        if (Array.isArray(x))
          return x.map(x => typeof x === "number" ? x : Number(x));

        return [];
      });

      const startedAt = tryGet(["timelapse", "startedAt"], Date.now(), (x) => {
        return typeof x === "number" ? x : Number(x);
      });

      const sessions: number[] = [];

      // The sessions array is just a shortcut - we can recompute it by listing the contents of the directory
      // and querying all files in the Lapse directory with the format of "session-<ID>.webm".
      for await (const [filename] of dir.entries()) {
        const match = /session-([0-9]+)\.webm/.exec(filename);
        if (!match) {
          console.log(`(deviceStorage.ts) ignoring file ${filename} for session restore`);
          continue;
        }

        console.log(`(deviceStorage.ts) session found for restore: ${filename} (ID ${match[1]})`);
        sessions.push(parseInt(match[1], 10));
      }

      if (sessions.length == 0) {
        console.warn("(deviceStorage.ts) no sessions found while restoring timelapse. no timelapse has been started...?");
        
        const store = {
          devices,
          timelapse: null
        };
        
        await this.writeStore(store);
        return store;
      }

      sessions.sort(ascending());

      console.log("(deviceStorage.ts) existing timelapse successfully recovered from corrupted JSON!");
      console.log("(deviceStorage.ts) recovery payload:", devices, snapshots, startedAt, sessions);
      
      const data = {
        devices,
        timelapse: {
          snapshots,
          startedAt,
          sessions
        }
      };

      await this.writeStore(data);
      return data;
    }
  }

  private async writeStore(data: Store): Promise<void> {
    const dir = await this.getLapseDir();
    const fileHandle = await dir.getFileHandle("store.json", { create: true });
    if (!("createWritable" in Object.getPrototypeOf(fileHandle))) {
      // We already handle this with the prototype check, but apparently old Safari lies to us.
      handleMissingApi(true);
      await sleep(10000);
      return;
    }

    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(data));
    await writable.close();
  }

  private async operation<T>(block: () => Promise<T>) {
    const result = await retryable("deviceStorage operation", async () => {
      return await this.serialQueue.enqueue(async () => {
        while (true) {
          try {
            return await block();
          }
          catch (error) {
            if (error instanceof DOMException) {
              if (error.name === "NotFoundError") {
                throw error;
              }

              await sleep(500); // DOMExceptions are usually odd browser state. we should be able to safely retry
              continue;
            }

            throw error;
          }
        }
      });
    });

    if (result instanceof Error) {
      throw result;
    }

    return result;
  }

  /**
   * Imports a timelapse from a potentially foreign source.
   */
  async importTimelapse(metadata: LocalTimelapse, sessions: (readonly [number, Blob])[]) {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      store.timelapse = metadata;

      const dir = await this.getLapseDir();
      for (const [sessionId, blob] of sessions) {
        const fileHandle = await dir.getFileHandle(`session-${sessionId}.webm`, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      }

      await this.writeStore(store);
      console.log("(deviceStorage.ts) imported timelapse data:", store.timelapse);

      return store.timelapse;
    });
  }

  async createTimelapse(): Promise<StoredTimelapse> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      store.timelapse = {
        startedAt: Date.now(),
        snapshots: [],
        sessions: []
      };

      await this.writeStore(store);
      console.log("(deviceStorage.ts) timelapse created:", store.timelapse);

      return store.timelapse;
    });
  }

  async getTimelapse(): Promise<StoredTimelapse | null> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      if (!store.timelapse)
        return null;

      return store.timelapse;
    });
  }

  async getTimelapseVideoSessions(): Promise<Blob[]> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      if (!store.timelapse)
        return [];

      const dir = await this.getLapseDir();
      const blobs: Blob[] = [];

      for (const sessionId of store.timelapse.sessions) {
        try {
          const fileHandle = await dir.getFileHandle(`session-${sessionId}.webm`);
          blobs.push(await fileHandle.getFile());
        }
        catch (err) {
          console.warn(`(deviceStorage.ts) could not read session ${sessionId} - skipping`, err);
        }
      }

      return blobs;
    });
  }

  async getTimelapseVideoSize(): Promise<number> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      if (!store.timelapse)
        return 0;

      const dir = await this.getLapseDir();
      let totalSize = 0;

      for (const sessionId of store.timelapse.sessions) {
        try {
          const fileHandle = await dir.getFileHandle(`session-${sessionId}.webm`);
          const file = await fileHandle.getFile();
          totalSize += file.size;
        }
        catch (err) {
          console.warn(`(deviceStorage.ts) could not read session ${sessionId} size - skipping`, err);
        }
      }

      return totalSize;
    });
  }

  async appendChunk(sessionId: number, chunk: Blob): Promise<void> {
    console.debug(`(deviceStorage.ts) appendChunk(${sessionId}) ->`, chunk);

    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      if (!store.timelapse) {
        console.warn(`(deviceStorage.ts) attempted to call appendChunk, but no timelapse is registered!`, chunk);
        return;
      }

      if (!store.timelapse.sessions.includes(sessionId)) {
        store.timelapse.sessions.push(sessionId);
        await this.writeStore(store);
      }

      const dir = await this.getLapseDir();
      const fileHandle = await dir.getFileHandle(`session-${sessionId}.webm`, { create: true });
      const file = await fileHandle.getFile();
      const writable = await fileHandle.createWritable({ keepExistingData: true });
      await writable.seek(file.size);
      await writable.write(chunk);
      await writable.close();
    });
  }

  /**
   * Drops the stored timelapse's metadata and removes all of its session files. Internal: the caller must already
   * be inside an `operation` block (this does not serialize or `ensureInit` on its own).
   */
  private async removeStoredTimelapse(): Promise<void> {
    const store = await this.readStore();
    const sessions = store.timelapse?.sessions ?? [];
    store.timelapse = null;
    await this.writeStore(store);

    const dir = await this.getLapseDir();
    for (const sessionId of sessions) {
      try {
        await dir.removeEntry(`session-${sessionId}.webm`);
      }
      catch (err) {
        console.warn(`(deviceStorage.ts) could not delete session ${sessionId}`, err);
      }
    }
  }

  async deleteTimelapse(): Promise<void> {
    return await this.operation(async () => {
      await this.ensureInit();
      await this.removeStoredTimelapse();
      console.log("(deviceStorage.ts) locally stored timelapse deleted");
    });
  }

  /**
   * Marks the locally stored timelapse as restored (published) under restore-logic version `version`, WITHOUT
   * deleting it. We keep the recording in OPFS as a safety net in case server-side processing turns out to be
   * faulty - bumping the restore version then resurfaces it for re-publish. `cleanupRestoredTimelapse` reclaims
   * the space later, once we're confident.
   */
  async markTimelapseRestored(version: number): Promise<void> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      if (!store.timelapse) {
        console.warn("(deviceStorage.ts) markTimelapseRestored called, but no timelapse is registered");
        return;
      }

      store.timelapse.restoredAt = Date.now();
      store.timelapse.restoredVersion = version;
      await this.writeStore(store);

      console.log(`(deviceStorage.ts) locally stored timelapse marked restored (v${version})`);
    });
  }

  /**
   * Sweeps the locally stored timelapse only if it was restored at least `maxAgeMs` ago under restore-logic version
   * `version` (or newer), reclaiming the OPFS space kept as a post-restore safety net. No-op otherwise - in
   * particular, a copy whose `restoredVersion` is older than `version` is pending re-recovery and is left intact.
   *
   * NOTE: deliberately not wired to any caller yet - the sweep ships in a later change.
   */
  async cleanupRestoredTimelapse(version: number, maxAgeMs: number): Promise<void> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      const local = store.timelapse;
      if (!local || local.restoredAt == null || local.restoredVersion == null)
        return;

      // A copy restored under an older version is pending re-recovery (the version was bumped because that restore
      // was deemed faulty) - never sweep it.
      if (local.restoredVersion < version)
        return;

      if (Date.now() - local.restoredAt < maxAgeMs)
        return;

      await this.removeStoredTimelapse();
      console.log("(deviceStorage.ts) swept restored timelapse from local storage");
    });
  }

  async appendSnapshot(timestamp: number): Promise<void> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      if (!store.timelapse)
        return;

      store.timelapse.snapshots.push(timestamp);
      await this.writeStore(store);

      console.debug("(deviceStorage.ts) appendSnapshot ->", timestamp);
    });
  }

  async saveDevice(device: LocalDevice): Promise<string> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      const existingIndex = store.devices.findIndex(d => d.id === device.id);

      if (existingIndex >= 0) {
        store.devices[existingIndex] = device;
      }
      else {
        store.devices.push(device);
      }

      await this.writeStore(store);

      console.log("(deviceStorage.ts) saveDevice ->", device);
      return device.id;
    });
  }

  async getDevice(id: string): Promise<LocalDevice | null> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      return store.devices.find(d => d.id === id) ?? null;
    });
  }

  async getAllDevices(): Promise<LocalDevices> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      return store.devices;
    });
  }

  async deleteDevice(id: string): Promise<void> {
    return await this.operation(async () => {
      await this.ensureInit();

      const store = await this.readStore();
      store.devices = store.devices.filter(d => d.id !== id);
      await this.writeStore(store);

      console.log(`(deviceStorage.ts) deleteDevice(${id})`);
    });
  }

  /**
   * Ensures all pending storage operations are complete.
   */
  async sync() {
    await this.serialQueue.synchronize();
    console.log("(deviceStorage.ts) device storage has been synchronized");
  }
}

/**
 * Allows for storing persistent binary data directly on the user's device.
 */
export const deviceStorage = new DeviceStorage();
